import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Collections } from '../collections.model';
import { BackendPushResponse, BackendResponse, StoreBackend } from './backend';
import { ObjectObservable } from './object-observable';
import { RequestStatus } from './request-status-handler.service';
import { Reference } from './store.model';

export class ListObservable<K extends keyof Collections> extends Rx.Observable<
  ReadonlyArray<Collections[K]>
> {
  constructor(
    private ref$: Rx.Observable<Reference<K>>,
    private storeBackend: StoreBackend,
    public status$: Rx.Observable<RequestStatus>,
    source$: Rx.Observable<ReadonlyArray<Collections[K]>>,
  ) {
    super();
    this.source = source$;
  }

  push(object: Collections[K]): Promise<BackendPushResponse<K>> {
    return this.ref$
      .pipe(take(1), switchMap(ref => this.storeBackend.push(ref, object)))
      .toPromise();
  }

  set(id: number | string, object: Collections[K]): Promise<BackendResponse> {
    return this.ref$
      .pipe(take(1), switchMap(ref => this.storeBackend.set(ref, id, object)))
      .toPromise();
  }

  update(
    id: number | string,
    object: Partial<Collections[K]>,
  ): Promise<BackendResponse> {
    return this.ref$
      .pipe(
        take(1),
        switchMap(ref => this.storeBackend.update(ref, id, object)),
      )
      .toPromise();
  }

  remove(id: number | string): Promise<BackendResponse> {
    return this.ref$
      .pipe(take(1), switchMap(ref => this.storeBackend.delete(ref, id)))
      .toPromise();
  }

  toObjectObservableList(): Rx.Observable<ReadonlyArray<ObjectObservable<K>>> {
    return this.source.pipe(
      map(collections =>
        collections.map(
          (collection: any) =>
            new ObjectObservable<K>(
              this.ref$.pipe(
                map(ref => ({
                  path: {
                    ...ref.path,
                    ids: [collection.id.toString()] as [string], // Type cast to get arity right
                  },
                })),
              ),
              this.storeBackend,
              this.status$,
              Rx.of(collection),
            ),
        ),
      ),
    );
  }
}
